import React, { useEffect } from 'react';
import { Briefcase, Search, Info, Copy, Check } from 'lucide-react';

interface PortfolioProps {
  walletAddress?: string;
  onShowConnectModal: () => void;
}

const Portfolio: React.FC<PortfolioProps> = ({ walletAddress, onShowConnectModal }) => {
  const [copiedAddress, setCopiedAddress] = React.useState(false);

  useEffect(() => {
    // Create subtle animated background particles
    const createBackgroundParticles = () => {
      const container = document.createElement('div');
      container.className = 'absolute inset-0 overflow-hidden pointer-events-none';
      
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        const size = Math.random() * 4 + 2;
        
        particle.className = 'absolute rounded-full bg-[#c19411] opacity-10';
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.top = `${Math.random() * 100}%`;
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.animation = `float ${Math.random() * 15 + 10}s linear infinite`;
        particle.style.animationDelay = `-${Math.random() * 10}s`;
        
        container.appendChild(particle);
      }
      
      document.querySelector('.portfolio-container')?.appendChild(container);
    };
    
    createBackgroundParticles();
  }, []);

  // Format wallet address for display
  const formatWalletAddress = (address: string | undefined) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const copyWalletAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] w-full portfolio-container relative">
      {/* Gradient background */}

      
      <div className="flex h-full">
        {/* Left Sidebar - Matches Wallets page layout */}
        <div className="backdrop-blur-sm bg-dark-grey/40 border-r border-gray-700/30 w-56 py-3 px-2.5 flex flex-col">
          <div className="flex items-center gap-2 mb-4 mt-2 px-1.5">
            <div className="p-1.5 rounded-lg bg-[#c19411]/20 text-[#c19411]">
              <Briefcase className="w-4 h-4" />
            </div>
            <h1 className="text-base font-bold text-white/90">Portfolio</h1>
          </div>
          
          {/* Connected Wallet Status */}
          <div className="mb-3 py-2 px-2.5 rounded-md backdrop-blur-sm bg-dark-grey/30 border border-gray-700/20">
            {walletAddress ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-300">{formatWalletAddress(walletAddress)}</span>
                </div>
                <button 
                  onClick={copyWalletAddress}
                  className="text-gray-400 hover:text-white transition-colors duration-200" 
                  title="Copy Address"
                >
                  {copiedAddress ? (
                    <Check size={12} className="text-green-400" />
                  ) : (
                    <Copy size={12} />
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={onShowConnectModal}
                className="flex items-center justify-center w-full gap-1.5 text-xs text-[#c19411] hover:text-[#c19411]/80 transition-colors duration-200"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                <span>Connect Wallet</span>
              </button>
            )}
          </div>
          
          <div className="relative mb-3">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <Search size={14} className="text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Search assets..." 
              className="w-full pl-8 pr-2 py-1.5 rounded-md backdrop-blur-sm bg-dark-grey/30 text-xs border border-gray-700/30 focus:border-[#c19411]/50 focus:outline-none transition-colors"
            />
          </div>
          
          {/* Info Notice - matches position in Wallet sidebar */}
          <div className="mb-3 py-2 px-2.5 text-xs rounded-md backdrop-blur-sm bg-[#c19411]/10 border border-[#c19411]/20 z-10 relative">
            <div className="flex items-start gap-2">
              <div className="p-1 rounded-full bg-[#c19411]/20 text-[#c19411] mt-0.5 flex-shrink-0">
                <Info size={10} />
              </div>
              <div>
                <h3 className="font-medium mb-0.5 text-[#c19411]">Coming Soon</h3>
                <p className="text-gray-400 text-[11px]">
                  Portfolio features are currently under development. Check back soon for updates!
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 p-4 flex items-center justify-center">
          {walletAddress ? (
            <div className="text-center max-w-md">
              <h2 className="text-2xl font-bold mb-2">Portfolio Coming Soon</h2>
              <p className="text-gray-400 mb-4">
                The portfolio features are currently in development. Check back soon for updates!
              </p>
              <div className="glass p-3 rounded-lg border border-[#c19411]/20 bg-[#c19411]/5">
                <p className="text-sm mb-1">Connected Wallet:</p>
                <p className="font-mono text-[#c19411]">{walletAddress}</p>
              </div>
            </div>
          ) : (
            <div className="text-center max-w-md">
              <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
              <p className="text-gray-400 mb-4">
                Please connect a wallet to view your portfolio data.
              </p>
              <button
                onClick={onShowConnectModal}
                className="px-4 py-2 bg-[#c19411]/20 text-[#c19411] rounded-lg hover:bg-[#c19411]/30 transition-colors"
              >
                Connect Wallet
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;